/**
 * Order of business for the Rebuy bundle popup (widget 289411, "Complete Your Setup") on the PDP
 * that renders it: the popup first, the cart drawer only once the shopper has answered it.
 *
 * Getting there means holding the drawer shut. It opens because
 * sections/cart-drawer-custom.liquid renders `auto-open` on <cart-drawer-component>, and
 * assets/cart-drawer.js acts on that at `submit` time — ~160ms after the click, before
 * /cart/add.js has even answered. Rebuy is a third-party script that reaches `rebuy.ready`
 * anywhere from 1s to 14s after navigation (measured on this PDP), so suppression keyed on Rebuy
 * being ready loses that race: the drawer opens first and the popup lands on top of it.
 *
 * The hold is therefore the default here, applied the moment this module runs, and it stays on for
 * as long as the popup is live — Rebuy re-offers the bundle on EVERY add, so a hold that lifted
 * after the first one left the second add opening the drawer 167ms in. That is not just untidy:
 * an open drawer paints `.cart-drawer__overlay` across the viewport, above the popup, so every
 * click meant for a bundle hits the overlay and closes the drawer instead — the shopper picks a
 * bundle and nothing is added. Keeping the drawer shut is what keeps the popup clickable.
 *
 * The drawer opens on our terms instead, without lifting the hold:
 *   - the shopper answers the popup (`rebuy.add` / `rebuy.hide`)
 *   - an add lands and no popup follows it (see GRACE below) — an add must never end with neither
 *     the popup nor the cart on screen
 *
 * The hold lifts, restoring `auto-open` exactly as the merchant configured it, only once there is
 * no popup left to protect: Rebuy reports the widget has nothing to offer for this product
 * (`rebuy.ready` with 0 products), or Rebuy never turns up at all (WATCHDOG).
 *
 * The hold is re-applied on mutation because assets/section-renderer.js morphs the drawer section
 * from fresh Liquid on every cart render, which puts `auto-open` straight back.
 *
 * This module has a second, unrelated job on the same popup: while the swap runs, putting every
 * cart write on the page through a single-file lane — Rebuy's two, the theme's, and any app's —
 * because Shopify's cart endpoints do not serialise and overlapping writes clobber each other. It
 * also clamps Rebuy's whole-line removal to the one unit being upgraded. Both are self-contained at
 * the bottom of the file, under a banner comment carrying the measurements behind them.
 *
 * Loaded from snippets/sl-bundle-upsell.liquid, so it exists exactly where the widget does: the
 * product template carrying the `rebuy-widget` block, and — when the merchant switches on
 * `bundle_upsell_cards_enabled` — the page-wide mount in layout/theme.liquid that serves adds
 * starting from a product card.
 *
 * Those two are not the same problem, and `inCardMode()` below is the switch between them. On a
 * product page the widget knows its one product up front; on a card page it starts empty and is
 * primed per add by assets/sl-bundle-upsell-cards.js, so "the widget has no products" means
 * "nothing to offer" in the first case and "nothing has been asked for yet" in the second.
 */

/** @type {string} Rebuy widget id for the bundle popup. */
const WIDGET_ID = '289411';

/** @type {string} The attribute assets/cart-drawer.js reads to decide whether an add opens the drawer. */
const AUTO_OPEN = 'auto-open';

/**
 * @type {string} Marks a product card whose product the popup has an offer for. Written by
 * `snippets/bundle-upsell-attribute.liquid`; absent on the product page, which is what tells this
 * module which of its two modes it is in.
 */
const CARD_GATE = '[data-bundle-upsell]';

/**
 * @type {string} The RX modals: the legacy `#rx-modal` and the prescription popup
 * (snippets/rx-choice-modal.liquid). Either one owns the screen after the add that opens it, and
 * decides when the cart drawer — or this popup — comes next (docs/decisions/DEC-034).
 */
const RX_MODAL = ':is(#rx-modal, [data-rx-modal])';

/**
 * @returns {boolean} Whether this page offers the bundle from CARDS (collection grid, home rows)
 * rather than from the one product of a product page.
 *
 * The two modes need opposite defaults. On a product page the widget resolves its products up front,
 * so "no products" genuinely means "no popup is ever coming" and the drawer can be handed back. On a
 * card page one widget serves every card and starts EMPTY by construction — it only gets products
 * once `assets/sl-bundle-upsell-cards.js` primes it for the add in flight. Reading emptiness as "no
 * popup" there would hand the drawer back permanently on page load, and the first qualifying add
 * would then open the drawer over the popup, whose overlay eats every click meant for a bundle.
 *
 * So in card mode the hold is never lifted by the widget being empty; each add decides for itself,
 * at `submit`, from the gate attribute.
 */
const inCardMode = () => document.querySelector(CARD_GATE) !== null;

/**
 * @type {number} How long an add waits for the popup before the drawer opens instead. Measured
 * gap between `cart:update` and `rebuy.show` on this PDP: ~500ms.
 */
const GRACE = 1500;

/**
 * @type {number} How long to wait for Rebuy before handing the drawer back. Only covers the idle
 * case (blocked script, no widget); an add that arrives first is resolved by `popupIsLive()`.
 */
const WATCHDOG = 15000;

/**
 * @type {number} How long to leave the drawer to assets/cart-drawer.js after the shopper answers
 * the popup, before opening it here anyway.
 *
 * Deliberately longer than that module's own DRAWER_REFRESH_BUDGET (900ms): it opens the drawer on
 * a fresh cart render, and this must not pre-empt it and put the stale-cart window back.
 */
const DRAWER_REFRESH_FALLBACK = 1200;

/** @type {boolean} Whether the drawer is currently held shut for the popup. */
let holding = true;

/** @type {boolean | null} `auto-open` as the merchant set it, read before the first removal. */
let autoOpenConfigured = null;

/** @type {boolean} Whether `rebuy.ready` has been seen for this widget. */
let widgetReady = false;

/** @type {boolean} Card mode: whether the add currently in flight came from a card with an offer. */
let qualifyingAddInFlight = false;

/** @type {boolean} Card mode: whether the add currently in flight asks the prescription question first. */
let rxAddInFlight = false;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let graceTimer;

/** @returns {Element | null} The cart drawer, re-queried each time because section renders replace it. */
const drawer = () => document.querySelector('cart-drawer-component');

/**
 * @param {Event} event
 * @returns {boolean} Whether the event belongs to the bundle popup widget.
 */
const isBundleWidget = (event) => String(/** @type {any} */ (event).detail?.widget?.id) === WIDGET_ID;

/** @returns {any} The mounted Rebuy widget, once its Vue app exists. */
const widget = () => /** @type {any} */ (document.querySelector(`[data-rebuy-id="${WIDGET_ID}"]`))?.__vue__;

/**
 * @returns {boolean} Whether a popup is still coming — Rebuy only pops for an add it was loaded in
 * time to see.
 *
 * `rebuy.ready` fires once, and this module is deferred, so on a slow page Rebuy gets there first
 * and the event is missed entirely. Asking the mounted widget covers that: the event is the fast
 * path, the widget itself is the authority.
 */
function popupIsLive() {
  return widgetReady || Boolean(widget()?.products?.length);
}

/** Strips `auto-open` while the hold is on, remembering how the merchant had it. */
function applyHold() {
  const element = drawer();
  if (!element) return;

  if (autoOpenConfigured === null) autoOpenConfigured = element.hasAttribute(AUTO_OPEN);

  // The merchant does not auto-open the drawer on an add: there is nothing to hold back.
  if (!autoOpenConfigured) {
    holding = false;
    return;
  }

  if (holding) element.removeAttribute(AUTO_OPEN);
}

/** Re-arms the hold after WATCHDOG lifted it. Rebuy readying the widget is the authority. */
function rearm() {
  holding = true;
  applyHold();
}

/**
 * Shows the cart, the hold untouched — this is how the drawer opens while the popup is in play.
 * Opening an already-open drawer is a no-op, and assets/cart-drawer.js opens it itself on
 * `rebuy.add`, so this is the guard that keeps the two from fighting.
 */
function openDrawer() {
  clearTimeout(graceTimer);

  const element = drawer();
  if (element && !element.querySelector('dialog[open]')) /** @type {any} */ (element).open?.();
}

/** Hands the drawer back to the theme. Only for when there is no popup left to protect. */
function release() {
  clearTimeout(graceTimer);
  holding = false;

  const element = drawer();
  if (element && autoOpenConfigured) element.setAttribute(AUTO_OPEN, '');
}

// This module runs from inside the product section, so the drawer — rendered by
// sections/cart-drawer-custom.liquid — may not be in the document yet. Re-apply on each milestone
// rather than assuming it is: missing the first pass leaves `auto-open` untouched for the whole page.
applyHold();
customElements.whenDefined('cart-drawer-component').then(applyHold);
document.addEventListener('DOMContentLoaded', applyHold, { once: true });
window.addEventListener('load', applyHold, { once: true });

// Subtree + attributeFilter keeps this cheap: the callback runs only when that one attribute
// changes anywhere in the document, which is what a morphed drawer section does.
new MutationObserver(() => {
  if (holding) applyHold();
}).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: [AUTO_OPEN] });

document.addEventListener('rebuy.ready', (event) => {
  if (!isBundleWidget(event)) return;

  widgetReady = true;

  // Rebuy can take upwards of 15s on a cold load, by which point WATCHDOG may already have handed
  // the drawer back. Re-take it: a popup is coming for the next add after all.
  if (/** @type {any} */ (event).detail?.widget?.data?.products?.length) rearm();
  // In card mode an empty widget is the normal resting state, not the absence of an offer.
  else if (inCardMode()) rearm();
  else release();
});

/**
 * Card mode only: decide this one add. The drawer is held by default, so `cart-drawer.js`'s own
 * optimistic open has already bailed on the missing `auto-open` — which is what makes this
 * independent of the order the two submit listeners happen to be registered in.
 *
 *   qualifying add  → keep holding; the popup owns the screen, as on a product page
 *   prescription add → open nothing; the RX modal owns the screen and opens the cart when it closes.
 *                     Opening it now put the drawer under the prescription popup (measured on home,
 *                     quick-add of AirSense 10, 2026-10-13). `cart:update` opens it when no modal came.
 *   anything else   → open the cart now, exactly the feedback the theme gives today
 *
 * The hold itself is never lifted here: the NEXT add may well be a qualifying one.
 */
document.addEventListener(
  'submit',
  (event) => {
    if (!holding || !inCardMode()) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    // An add made from inside an RX modal opens nothing: the modal's close decides what comes next.
    if (form.closest(RX_MODAL)) return;

    qualifyingAddInFlight = Boolean(form.closest(CARD_GATE));
    rxAddInFlight = asksForPrescription(form);
    if (!qualifyingAddInFlight && !rxAddInFlight) openDrawer();
  },
  { capture: true }
);

// The popup is up: the shopper now decides when the cart opens, so let the grace period lapse.
/**
 * @type {number} How long to keep guarding clicks after the popup appears, if it never confirms.
 * Measured window on home: 326-353ms. The cap is generous enough to cover a slower device and short
 * enough that a shopper who wants to click elsewhere is never held up for long.
 */
const CLICK_GUARD_CAP = 1200;

/** @type {string[]} The events a stray click arrives on, all cancelled together. */
const CLICK_GUARD_EVENTS = ['pointerdown', 'mousedown', 'click'];

/**
 * @returns {boolean} Whether a click aimed at a bundle button actually reaches it.
 *
 * Hit testing lags layout. Measured on home, sampling every 25ms from `rebuy.show`: for the first
 * ~330ms `document.elementsFromPoint` at the centre of a bundle button returns the PAGE behind the
 * popup — `DIV.hero-split__content` — and the button is not in the returned stack at all, while its
 * rect, its computed `pointer-events`, opacity, transform and every ancestor's are already
 * identical to what they are once it works. Nothing about the popup changes at the moment it starts
 * working, so this is the browser's hit-test regions catching up with a newly painted fixed layer,
 * not a style this theme can correct.
 */
function popupTakesClicks() {
  const button = document.querySelector(`#rebuy-widget-${WIDGET_ID} .rebuy-button`);
  if (!button) return false;

  const box = button.getBoundingClientRect();
  if (!box.width || !box.height) return false;

  return document
    .elementsFromPoint(box.left + box.width / 2, box.top + box.height / 2)
    .some((element) => element === button || button.contains(element));
}

/**
 * Swallows clicks that LOOK like they landed on the popup but resolve to the page behind it.
 *
 * Without this, the first ~330ms of the popup's life are a trap: the shopper sees the offer, clicks
 * a bundle, and the click goes to whatever the popup is covering — on a collection grid or a home
 * row that is another product's card, and its add button. "I picked a bundle and it put a different
 * product in my cart" is exactly that.
 *
 * Scoped to the popup's own box on purpose. A click anywhere else on the page is genuinely meant for
 * the page and still works; only a click the shopper aimed at the popup is cancelled, and only until
 * the popup can take it. One lost click is a far better outcome than the wrong line item.
 */
function guardStrayClicksUntilPopupTakesThem() {
  /** @param {Event} event */
  const swallow = (event) => {
    const target = event.target;
    // Already reaching the popup: nothing to protect against.
    if (target instanceof Element && target.closest(`[data-rebuy-id="${WIDGET_ID}"]`)) return;

    const content = document.querySelector(`#rebuy-widget-${WIDGET_ID} .rebuy-widget-content`);
    if (!content) return;

    const box = content.getBoundingClientRect();
    const { clientX, clientY } = /** @type {MouseEvent} */ (event);
    if (clientX < box.left || clientX > box.right || clientY < box.top || clientY > box.bottom) return;

    event.preventDefault();
    event.stopPropagation();
  };

  for (const type of CLICK_GUARD_EVENTS) document.addEventListener(type, swallow, true);

  const deadline = Date.now() + CLICK_GUARD_CAP;
  const release = () => {
    for (const type of CLICK_GUARD_EVENTS) document.removeEventListener(type, swallow, true);
  };

  const check = () => {
    if (popupTakesClicks() || Date.now() > deadline) {
      release();
      return;
    }
    requestAnimationFrame(check);
  };
  requestAnimationFrame(check);
}

/**
 * @param {HTMLFormElement} form An add-to-cart form being submitted.
 * @returns {boolean} Whether its add asks the prescription question before anything else: the same
 * test assets/cart-drawer.js makes before opening the drawer on submit. The variant sold without a
 * prescription is exempt (assets/product-form.js → rxExempt).
 */
function asksForPrescription(form) {
  const component = /** @type {any} */ (form.closest('product-form-component'));
  return Boolean(component?.hasAttribute('data-rxupsell') && !component.rxExempt);
}

/** @returns {boolean} Whether an RX modal owns the screen: open, or about to open for the add in flight. */
function rxModalOwnsScreen() {
  return (
    document.body.hasAttribute('data-rx-modal-pending') ||
    Boolean(document.querySelector(`${RX_MODAL} dialog[open]`))
  );
}

/**
 * Arms the click guard once no RX modal owns the screen — straight away when none does.
 *
 * The add that fired this popup may be asking the prescription question first: the RX modal is open,
 * or about to open, and this popup waits out of sight meanwhile (assets/rx-choice-modal.css). Its box
 * sits right under that modal, so a guard armed now would only swallow the clicks meant for the
 * modal's own buttons. The prescription popup signals through attributes on <body>; the legacy modal
 * only through `dialog:close` (which does not bubble, hence capture). Both are watched.
 */
function guardOnceRxModalIsDone() {
  if (!rxModalOwnsScreen()) {
    guardStrayClicksUntilPopupTakesThem();
    return;
  }

  const observer = new MutationObserver(() => check());
  const check = () => {
    if (rxModalOwnsScreen()) return;
    observer.disconnect();
    document.removeEventListener('dialog:close', check, true);
    guardStrayClicksUntilPopupTakesThem();
  };
  observer.observe(document.body, { attributes: true, attributeFilter: ['data-rx-modal-pending', 'data-rx-modal-open'] });
  document.addEventListener('dialog:close', check, true);
}

/**
 * @type {string} The root of the Alia email-capture popup.
 *
 * Matched by id prefix because the suffix is the Alia account number
 * (`#alia-root-307193` on this store) — stable enough to target, not stable enough to hardcode.
 */
const ALIA_ROOT = '[id^="alia-root-"]';


/**
 * @type {Map<HTMLElement, { value: string, priority: string }>} Overlays hidden for the popup, each
 * with the inline `display` it had, so it is put back exactly as its own script left it.
 *
 * Restoring by removing the property would NOT do: Alia drives its popup from inline `display`
 * (measured — both of its roots sit at inline `display: none` while idle), so dropping the property
 * would hand it back a state it never set and could leave its popup stuck.
 */
const hiddenOverlays = new Map();

/** @type {number} How often to re-apply the hold and check that the popup still earns it. */
const OVERLAY_HOLD_POLL = 250;

/** @type {ReturnType<typeof setInterval> | undefined} */
let overlayHoldTimer;

/**
 * Keeps the purchase step to itself while the bundle popup is open.
 *
 * MEASURED, and it is the whole reported bug: Alia's popup is a full-viewport `position: fixed`
 * overlay at `z-index: 2147483647` — the SAME maximum this popup uses — injected into `<body>`
 * LATER, so it wins the tie on DOM order. It then covers the bundle offer completely and takes
 * every click aimed at it. Probed on the preview with the popup open: all three bundle buttons, the
 * skip link and every point across the card resolved to `IMG#alia-…`, not one to the widget.
 *
 * It matches the report exactly. "Sometimes it works" is Alia not having fired yet; "it fails the
 * second time" is Alia's trigger delay landing around the second add; "worse on a bad connection"
 * is Alia loading later still. And it never showed up in scripted testing because `element.click()`
 * dispatches straight at the node and skips hit testing altogether — only a real cursor finds it.
 *
 * A shopper who has just added to cart is mid-purchase. Two full-screen modals cannot share that
 * moment, and of the two the one holding the offer they are answering wins. Alia is hidden, not
 * cancelled: it comes back the moment the offer is answered, so nothing is lost but the ordering.
 */
function holdOverlaysForPopup() {
  const hide = () => {
    for (const element of document.querySelectorAll(ALIA_ROOT)) {
      if (!(element instanceof HTMLElement)) continue;

      const value = element.style.getPropertyValue('display');
      const priority = element.style.getPropertyPriority('display');

      // Already ours — nothing to do, and nothing to record, or we would remember our own hold as
      // the state to restore.
      if (value === 'none' && priority === 'important') continue;

      // Anything else is the overlay's own intent, including a `display: block` it just set to open
      // itself ON TOP of the popup. Take the newest one: that is what has to come back afterwards.
      hiddenOverlays.set(element, { value, priority });
      element.style.setProperty('display', 'none', 'important');
    }
  };

  hide();

  // One poll, two jobs, because both have to keep working for the whole life of the popup.
  //
  // KEEP HIDING: Alia's trigger is delayed ~38s (measured on the preview), so the common case is it
  // firing while the popup is ALREADY up. It does that by flipping inline `display` on a root that
  // has been in the DOM since page load — no node is inserted — so a `childList` observer, which is
  // what this used to be, never sees it. Re-checking is what actually covers the case.
  //
  // AND RELEASE, on the POPUP being gone rather than only on the events that usually announce it. A
  // marketing overlay left hidden because Rebuy errored, or because the shopper navigated
  // mid-offer, would be a worse bug than the one this fixes — and it would be ours.
  clearInterval(overlayHoldTimer);
  overlayHoldTimer = setInterval(() => {
    if (!document.querySelector(`#rebuy-widget-${WIDGET_ID} .rebuy-widget-container.is-visible`)) {
      releaseOverlays();
      return;
    }
    hide();
  }, OVERLAY_HOLD_POLL);
}

/** Gives the screen back. Called on every path that ends the popup, including the watchdog. */
function releaseOverlays() {
  clearInterval(overlayHoldTimer);

  for (const [element, previous] of hiddenOverlays) {
    element.style.removeProperty('display');
    if (previous.value) element.style.setProperty('display', previous.value, previous.priority);
  }
  hiddenOverlays.clear();
}

document.addEventListener('rebuy.show', (event) => {
  if (!isBundleWidget(event)) return;

  clearTimeout(graceTimer);
  holdOverlaysForPopup();

  // Rebuy never unmounts the popup — hidden is only `visibility: hidden` — so it reopened wherever
  // its scroll was left: at the very bottom, after a shopper scrolled down to "skip" (measured on
  // the preview, 117 of 117px, 2026-09-24). Every offer starts from its title. Both boxes, because
  // the sheet scrolls on mobile and Rebuy's container on a short desktop window
  // (blocks/rebuy-widget.liquid). Still hidden at this point, so there is no visible jump.
  for (const box of document.querySelectorAll(
    `#rebuy-widget-${WIDGET_ID} .rebuy-widget-container, #rebuy-widget-${WIDGET_ID} .rebuy-widget-content`
  )) {
    box.scrollTop = 0;
  }

  // Belt and braces. The hold should mean the drawer is already shut, but if anything got it open
  // its overlay sits above the popup and eats every click meant for a bundle.
  const element = drawer();
  if (element?.querySelector('dialog[open]')) /** @type {any} */ (element).close?.();

  guardOnceRxModalIsDone();
});

// The check above misses a drawer that is still OPENING. assets/dialog.js shows the dialog one frame
// after `open()` (requestAnimationFrame), so when GRACE fires just before the popup arrives, the
// drawer lands a frame AFTER `rebuy.show` — open under the popup, `body` pinned `position: fixed`
// by its scroll lock. Measured on the preview 2026-09-24: popup 1.68s after the add, `dialog:open`
// 506ms after `rebuy.show`. That is the state in which the popup would not scroll on iOS Safari
// (see `isUnderAnotherRebuyPopup` in assets/cart-drawer.js). So close it on arrival.
// `dialog:open` does not bubble, hence capture. The shopper's own exits are unaffected: by the time
// their drawer opens, Vue has already taken `is-visible` off the popup. The header cart icon is one
// of those exits only because the popup's backdrop takes the tap (blocks/rebuy-widget.liquid): with
// the backdrop click-through, the icon opened the drawer straight into this handler, which closed
// it again — measured live 2026-11-03, and the reason a shopper saw the cart icon "do nothing".
document.addEventListener(
  'dialog:open',
  (event) => {
    const element = drawer();
    if (!element || event.target !== element) return;
    if (!document.querySelector(`#rebuy-widget-${WIDGET_ID} .rebuy-widget-container.is-visible`)) return;

    /** @type {any} */ (element).close?.();
  },
  { capture: true }
);

document.addEventListener('rebuy.add', (event) => {
  if (!isBundleWidget(event)) return;

  releaseOverlays();

  try {
    widget()?.hide?.();
  } catch (error) {
    // Losing the popup dismissal is survivable; leaving the shopper without the cart they just
    // added to is not, so this does not return early.
    console.error('Closing the bundle popup after an add failed:', error);
  }

  // NOT opened here and now. assets/cart-drawer.js opens the drawer on this same event, but only
  // once it has a fresh render of the cart — otherwise the drawer shows the cart as it was BEFORE
  // the swap, machine included, until the render lands (measured 421ms on the preview theme, and
  // longer the worse the connection). A shopper reading that sees the machine they just converted
  // still sitting there and concludes the swap failed. Opening it from here would put that window
  // straight back.
  //
  // This stays as the LATE fallback, for the case where that handler is not around to do it: an add
  // must never end with neither the popup nor the cart on screen. Opening an already-open drawer is
  // a no-op, so the two cannot fight.
  //
  // The hold stays armed: the next add gets the same popup, and needs the same protection.
  setTimeout(openDrawer, DRAWER_REFRESH_FALLBACK);
});

// "No thank you, skip for now", the X, or a click outside the card.
document.addEventListener('rebuy.hide', (event) => {
  if (!isBundleWidget(event)) return;

  releaseOverlays();
  reportSwapOutcome(false);

  openDrawer();
});

document.addEventListener('cart:update', (event) => {
  const { data } = /** @type {any} */ (event).detail ?? {};

  if (!holding || data?.didError) return;
  // Quantity edits inside the drawer come through here too; only an add from the buy button can
  // trigger the popup.
  if (data?.source !== 'product-form-component') return;
  // The RX upsell modal owns the screen after this add — the drawer must not open behind it.
  if (data?.upsellShown) return;
  // The service added from inside an RX modal: the modal's close opens the drawer, or leaves it to
  // this popup when it is waiting (assets/cart-drawer.js → #handleRxModalClose). A GRACE timer armed
  // here would open the drawer over the popup instead.
  if (data?.fromRxModal) return;

  if (inCardMode()) {
    // The submit handler already opened the cart for a non-qualifying add, except a prescription add:
    // no RX modal followed it (a service is already in the cart), so the cart is due now.
    if (!qualifyingAddInFlight) {
      if (rxAddInFlight) openDrawer();
      return;
    }

    // Tagged as a bundle product, but Rebuy came back with nothing to offer for it — a tag list is
    // coarser than Rebuy's own rules, so this is ordinary, not an error. Waiting out GRACE here
    // would put 1.5s of nothing between the click and the cart on every such add.
    //
    // Resolution was started at `submit`, roughly a second earlier, so by now it has almost always
    // landed. When it has not, opening is still safe: the hold is untouched and `rebuy.show` closes
    // the drawer again if the popup turns up late.
    if (!widget()?.products?.length) {
      openDrawer();
      return;
    }

    // A qualifying add with a real offer: the popup is coming. Give it the same grace a product page
    // gives, then fall back to the cart so an add never ends with neither on screen.
    clearTimeout(graceTimer);
    graceTimer = setTimeout(openDrawer, GRACE);
    return;
  }

  // No popup coming for this add — and none coming for any later one either: give the drawer back.
  if (!popupIsLive()) {
    release();
    openDrawer();
    return;
  }

  clearTimeout(graceTimer);
  graceTimer = setTimeout(openDrawer, GRACE);
});

setTimeout(() => {
  // Card mode: an empty widget is expected until an add primes it, so emptiness is not evidence
  // that Rebuy failed to turn up. Only a page whose popup was supposed to be ready by now releases.
  if (!popupIsLive() && !inCardMode()) release();
}, WATCHDOG);
/*
 * -------------------------------------------------------------------------------------------------
 * Second job: ONE cart write at a time while the swap runs, and keep the units the shopper is not
 * upgrading.
 *
 * The popup's add button calls Rebuy's `addUpsellToCart` (see snippets/sl-bundle-upsell.liquid),
 * which turns the machine into the bundle with TWO cart writes: `/cart/change.js` with
 * `quantity: 0` on the line that triggered the widget, then — about 500ms later, without waiting
 * for the first to answer — `/cart/add.js` for the bundle.
 *
 * OVERLAPPING CART WRITES CLOBBER EACH OTHER. Shopify's Ajax cart endpoints are read-modify-write
 * over the whole cart and do not serialise: whichever request writes last wins, carrying the
 * snapshot it read. Measured against this store on 2026-09-04, the same add+remove pair fired
 * 200-400ms apart, three runs in a row:
 *
 *   add 0->707ms (200)  · change 203->719ms (200)  → cart EMPTY, the add's write lost
 *   add 0->1104ms (200) · change 202->848ms (200)  → correct
 *   add 0->699ms (200)  · change 404->715ms (429)  → bundle PLUS the machine that should have gone
 *
 * Three different carts from three identical inputs. Both failures are real purchase-flow damage: a
 * shopper left with nothing, or one charged for the machine the upsell was meant to replace.
 *
 * ONE REQUEST WOULD BE BETTER, and the platform almost allows it: `/cart/update.js` takes an
 * `updates` map that can zero one line and add another variant in a single atomic write. It cannot
 * set line item PROPERTIES on the line it adds, and Rebuy's bundle line carries `_source`,
 * `_widget_id` and `_attribution` — its revenue attribution. Losing those to win atomicity is not a
 * trade this feature gets to make, so two writes is the floor and they have to be ordered instead.
 *
 * THE LANE below is how. While a swap is in play, every cart WRITE on the page — Rebuy's two, the
 * theme's, and any app's — goes through a single-file queue. That is deliberately wider than
 * Rebuy's own pair: this page has other cart writers (the Elevar web pixel POSTs `/cart/update.js`
 * with attributes after every add, measured at +900ms), and by the rule above any of them landing
 * mid-swap can clobber it just as effectively. The lane is armed only while `swapping` is set, or
 * while it still has work in it, so outside a swap nothing about this page's cart traffic changes.
 *
 * If the bundle add fails after the removal went through, the machine goes back — inside the same
 * lane slot, so the repair cannot collide either.
 *
 * The older job rides along here. `quantity: 0` is only right while that line holds a single unit:
 * Shopify merges an add into an existing line whenever variant AND properties match, so a shopper
 * who already owned a standalone machine and adds a second lands on ONE line at quantity 2, and
 * Rebuy takes both — losing the machine that was in the cart before the offer was ever made. The
 * removal is therefore clamped to the one unit being upgraded.
 *
 * The clamp has to know what the line holds WITHOUT a network read. A read on the critical path is
 * what stretched the overlap above from 236ms to 1.8s. The number is already on the wire:
 * `/cart/add.js` answers with the line it just wrote, at the quantity it ended up at. Every cart
 * response passing through here is therefore remembered, newest-wins. `cart:update` cannot supply
 * it — assets/product-form.js dispatches `new CartAddEvent({}, ...)`, an empty resource, which is
 * why the map this replaces was never populated and every swap silently took the slow path.
 * -------------------------------------------------------------------------------------------------
 */

/** @type {number} How long after a click on a bundle button Rebuy's removal is still ours to clamp. */
const SWAP_WINDOW = 5000;

/** @type {number} Pause before the one retry of the cart read. */
const READ_RETRY_DELAY = 250;

/**
 * @type {number} How long one write may hold the lane before the next is let through.
 *
 * A lane is only as safe as its escape hatch: a request that never settles would otherwise stop
 * every cart write on the page for the rest of the session. Ten seconds is past the theme's own
 * add-to-cart timeout, so in practice only a hung third-party write ever reaches it.
 */
const LANE_TIMEOUT = 10000;

/** @type {boolean} Whether a bundle add from this popup is in flight. */
let swapping = false;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let swapTimer;

/** @type {typeof fetch} The unwrapped fetch, so our own reads and repairs cannot re-enter this. */
const nativeFetch = window.fetch.bind(window);

/** @type {Promise<unknown>} The tail of the single-file queue every cart write joins during a swap. */
let lane = Promise.resolve();

/** @type {number} Writes currently queued or running in the lane. */
let laneDepth = 0;

/**
 * @typedef {object} CartLine
 * @property {number} quantity Units currently on the line.
 * @property {number} variantId The variant, needed to put the line back.
 * @property {Record<string, string>} properties Line properties — part of what makes the line key,
 *   so a repair without them would land on a DIFFERENT line.
 */

/** @type {Map<string, CartLine>} What each cart line holds, learned from responses passing through. */
const cartLines = new Map();

/**
 * @type {{ key: string, variantId: number, properties: Record<string, string>, cartUrl: string, addUrl: string, changeUrl: string, expected: number, at: number } | null}
 * The line this swap took units off, plus `expected` — the quantity it must be left holding once
 * the swap is done. Kept so a failed bundle add can put one back, and so the swap can check its own
 * work. Null when the line was never seen and the removal went out unclamped — there is nothing to
 * assert or restore accurately then, and guessing at a purchase is worse than leaving it alone.
 *
 * No quantities here on purpose: `restoreRemovedLine` reads the live cart instead of trusting a
 * remembered number, because the write it decides on is the one that could put a machine in the
 * cart the shopper never asked for.
 */
let removedLine = null;

/** Stops clamping. */
function disarmSwap() {
  clearTimeout(swapTimer);
  swapping = false;
}

// Capture phase: the clamp has to be armed before Vue's own click handler starts the swap. Both
// kinds of bundle button come through here — the "Size" ones add nothing on the first click and
// simply let SWAP_WINDOW lapse with no removal to clamp.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(`[data-rebuy-id="${WIDGET_ID}"] .rebuy-button`)) return;

    // A new attempt: clear whatever the last one said before it says anything of its own.
    reportSwapOutcome(false);

    swapping = true;
    clearTimeout(swapTimer);
    swapTimer = setTimeout(disarmSwap, SWAP_WINDOW);
  },
  { capture: true }
);

// The swap is over well before SWAP_WINDOW lapses, and the drawer is now open in front of the
// shopper: stop clamping so a "Remove" they click on a 2-unit line removes both units, as asked.
document.addEventListener('rebuy.add', (event) => {
  if (isBundleWidget(event)) disarmSwap();
});

/**
 * @type {number} Ticks once per cart request this wrapper observes, so a payload can be placed in
 * time relative to the others.
 */
let cartRequestSeq = 0;

/** @type {number} The newest request whose payload has been absorbed. */
let absorbedSeq = -1;

/**
 * Records what each line in a cart payload holds.
 * @param {any} payload A cart, the `{ items, sections }` an add answers with, or a single line.
 */
function rememberCartLines(payload) {
  if (!payload) return;

  const keep = (/** @type {any} */ item) => {
    if (!item || typeof item.key !== 'string' || typeof item.quantity !== 'number') return;
    cartLines.set(item.key, {
      quantity: item.quantity,
      variantId: item.variant_id ?? item.id,
      properties: item.properties ?? {},
    });
  };

  if (Array.isArray(payload.items)) {
    // A whole cart — `item_count` is what tells it apart from an add response, which carries only
    // the lines just written and must not wipe the rest of the map.
    if (typeof payload.item_count === 'number') cartLines.clear();
    for (const item of payload.items) keep(item);
    return;
  }

  keep(payload);
}

/**
 * Absorbs a cart payload, but ONLY if it is newer than the last one absorbed.
 *
 * Cart reads fly around this page constantly — the drawer, the cart icon, Rebuy and the shipping
 * app between them issue about a dozen per add — and they do not answer in the order they were
 * sent. Without this guard a read issued BEFORE an add can answer after it and overwrite the line
 * quantity the clamp is about to trust, because a whole-cart payload clears the map first. That is
 * the same class of stale-read bug this module exists to fix, one level up.
 *
 * A payload that teaches nothing must NOT advance the watermark. `cart:update` fires on every add
 * carrying an EMPTY resource (assets/product-form.js dispatches `new CartAddEvent({}, ...)`), and an
 * earlier version let that raise the watermark anyway — which then discarded the add response still
 * in flight behind it. The map starved, the clamp had no quantity to work from, and the removal went
 * out unclamped: measured on the preview, `quantity: 0` on a line holding TWO units, i.e. the
 * shopper losing a machine they already owned. That is the exact bug the clamp exists to prevent.
 *
 * @param {number} seq The value of `cartRequestSeq` when the request was issued.
 * @param {any} payload
 */
function absorbCart(seq, payload) {
  if (seq < absorbedSeq) return;
  if (!payload || (!Array.isArray(payload.items) && typeof payload.key !== 'string')) return;

  absorbedSeq = seq;
  rememberCartLines(payload);
}

/**
 * @param {Response} response
 * @param {number} seq
 */
function rememberFrom(response, seq) {
  // Deliberately not awaited: this must add no latency to a write the shopper is watching.
  response
    .clone()
    .json()
    .then((payload) => absorbCart(seq, payload))
    .catch(() => {
      // A cart response we could not parse simply teaches us nothing.
    });
}

document.addEventListener('cart:update', (event) => {
  // Only quantity edits inside the drawer carry a real cart here; an add carries `{}`. Harmless
  // either way, and it keeps the map honest after the shopper edits the cart by hand.
  absorbCart(++cartRequestSeq, /** @type {any} */ (event).detail?.resource);
});

/**
 * @param {string} lineKey
 * @param {string} cartUrl
 * @param {number} [attempts]
 * @returns {Promise<CartLine | null | undefined>} The line; `null` when the read succeeded and the
 * line is NOT in the cart; `undefined` when the cart could not be read at all.
 *
 * It returns the LINE rather than a quantity because the caller also needs the variant and the
 * properties to be able to put it back, and taking those from `cartLines` instead meant a read that
 * succeeded could still leave the clamp with nothing to work from.
 *
 * That distinction is load-bearing. Both used to come back as `null`, and the repair path read it as
 * "the line is gone" — so a cart it simply could not reach (a 429 is enough) made it add a machine
 * back that had never been removed. A write must never be decided on the absence of an answer.
 */
async function lineOnCart(lineKey, cartUrl, attempts = 2) {
  // Rebuy's own in-memory cart was tried as a fallback and dropped: it refreshes lazily and was
  // observed both stale AND empty, so it cannot be trusted to decide whether a line loses one unit
  // or all of them — the one thing this has to get right.
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt) await new Promise((resolve) => setTimeout(resolve, READ_RETRY_DELAY));

    const seq = ++cartRequestSeq;
    try {
      const response = await nativeFetch(cartUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) continue;

      const cart = await response.json();
      absorbCart(seq, cart);

      const line = cart?.items?.find((/** @type {{ key: string }} */ item) => item.key === lineKey);
      if (!line) return null;

      return { quantity: line.quantity, variantId: line.variant_id ?? line.id, properties: line.properties ?? {} };
    } catch {
      // Retry, then give up and let Rebuy's own request through untouched.
    }
  }

  return undefined;
}

/** @param {string} url @returns {boolean} */
const isCartAdd = (url) => /\/cart\/add\.js(\?|$)/.test(url);

/** @param {string} url @returns {boolean} */
const isCartChange = (url) => /\/cart\/change\.js(\?|$)/.test(url);

/** @param {string} url @returns {boolean} */
const isCartRead = (url) => /\/cart\.js(\?|$)/.test(url);

/**
 * @param {string} url
 * @param {RequestInit | undefined} init
 * @returns {boolean} Whether this request changes the cart, whoever is making it.
 */
const isCartWrite = (url, init) =>
  String(init?.method ?? 'GET').toUpperCase() === 'POST' &&
  /\/cart\/(add|change|update|clear)\.js(\?|$)/.test(url);

/**
 * Runs a cart write with no other cart write in flight.
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function inLane(task) {
  laneDepth += 1;
  const result = lane.then(task, task);
  const settled = result.then(
    () => {},
    () => {}
  );
  settled.then(() => {
    laneDepth -= 1;
  });

  // The lane advances on whichever comes first: the write settling, or LANE_TIMEOUT. A hung
  // request must not take the page's cart with it.
  lane = Promise.race([settled, new Promise((resolve) => setTimeout(resolve, LANE_TIMEOUT))]);

  return result;
}

/**
 * Puts back the unit the popup took off, after the bundle that was meant to replace it failed.
 *
 * A re-add rather than a `change.js`: the line is gone, so its key no longer resolves. Re-adding the
 * same variant with the same properties lands on the same key Shopify would have produced anyway.
 * Called from inside the failing add's lane slot, so it needs no lane of its own — and must not
 * take one, or it would wait on the slot it is already inside.
 */
async function restoreRemovedLine() {
  const line = removedLine;
  if (!line) return;

  try {
    // RECONCILE, never compensate blindly. This is a write on a failure path, and a wrong one puts
    // a machine in the cart the shopper never asked for — the same damage this module exists to
    // prevent, pointing the other way. An earlier version restored a fixed unit and did exactly
    // that when its cached quantity was stale.
    //
    // ONE attempt, no retry: the shopper is watching an add that has already failed, and a second
    // round trip buys accuracy nobody is waiting for.
    const stillThere = await lineOnCart(line.key, line.cartUrl, 1);

    // `undefined` means the cart could not be read. Not knowing is a reason to do nothing, never a
    // licence to write: the shopper can still see and fix their cart, and an unwanted machine in it
    // is the one outcome worse than a missing one.
    if (stillThere === undefined) {
      console.error('Could not read the cart to check whether the machine needs restoring; leaving it alone.');
      return;
    }
    if (stillThere !== null && stillThere.quantity >= 1) return;

    // ONE unit, not the whole line: the offer was per add, so one unit is what the popup took away.
    const seq = ++cartRequestSeq;
    const response = await nativeFetch(line.addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.variantId, quantity: 1, properties: line.properties }),
    });
    if (!response.ok) throw new Error(`restore answered ${response.status}`);

    rememberFrom(response, seq);
  } catch (error) {
    // Nothing further is safe to try: another write here would be the third one this swap, and the
    // shopper can still see and fix the cart. Report it loudly instead of pretending it worked.
    console.error('Restoring the machine after a failed bundle add failed:', error);
  }
}

/** @type {string} Set on <body> while the last swap attempt failed, so the popup can say so. */
const SWAP_FAILED_CLASS = 'sl-bundle-swap-failed';

/**
 * Tells the shopper the swap could not be written, and clears that the moment they try again.
 *
 * WHY THIS EXISTS: the popup had no failure state at all. Recorded on the preview during a real
 * session — the swap's `change.js` and `add.js` both answered 429, and the popup simply stayed
 * open. The shopper clicked the same bundle three times over sixty seconds, clicked outside it,
 * and nothing moved and nothing was said. "It froze" is exactly what that looks like, and it was
 * ours: every other failure path in this module reports itself, this one did not.
 *
 * The message is toggled by a class on <body> rather than by touching the popup's DOM, because that
 * DOM belongs to Rebuy's Vue app and is re-rendered whenever a product's status changes — the slot
 * itself is static markup in snippets/sl-bundle-upsell.liquid, so every re-render re-creates it and
 * the class keeps deciding whether it shows.
 *
 * @param {boolean} failed
 */
function reportSwapOutcome(failed) {
  document.body.classList.toggle(SWAP_FAILED_CLASS, failed);

  const slot = document.querySelector(`#rebuy-widget-${WIDGET_ID} [data-sl-swap-error]`);
  if (slot) slot.textContent = failed ? Theme?.translations?.add_to_cart_error || 'Error adding to cart' : '';
}

/**
 * Checks the swap actually did what it said, and puts it right once if it did not.
 *
 * WHY AN ASSERTION AND NOT MORE ORDERING: the lane orders every write it can SEE. It cannot order a
 * write that was already in flight when the swap began — this page has apps that POST to the cart
 * on their own schedule (the Elevar web pixel fires `/cart/update.js` about 900ms after every add),
 * and by the measurement in the banner above, any of them landing with a pre-removal snapshot puts
 * the machine straight back. Ordering cannot cover that. Verifying can.
 *
 * So the swap states its postcondition — the converted line ends up holding `expected` units — and
 * this checks it. On the happy path that is one cart read the shopper never waits for, because it
 * is deliberately NOT awaited: the popup has already closed and the drawer is already opening. A
 * correction lands within a few hundred milliseconds, and the drawer is re-rendered so what the
 * shopper reads is the cart that actually exists.
 *
 * It runs ONCE. If the correction itself does not hold, the module says so and stops: a third write
 * chasing the same line is how a purchase flow ends up somewhere nobody predicted.
 *
 * @param {NonNullable<typeof removedLine>} line
 */
async function reconcileSwap(line) {
  const found = await lineOnCart(line.key, line.cartUrl, 1);

  // Gone, already down to what the swap intended, or unreadable — the last of which is a reason to
  // do nothing rather than to write.
  if (!found || found.quantity <= line.expected) return;

  const actual = found.quantity;

  console.error(
    'Bundle swap did not stick: the converted line is still in the cart. Correcting once.',
    { line: line.key, expected: line.expected, actual }
  );

  try {
    const response = await inLane(() =>
      nativeFetch(line.changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.expected }),
      })
    );
    if (!response.ok) throw new Error(`correction answered ${response.status}`);

    rememberFrom(response, ++cartRequestSeq);

    // The drawer was rendered from the cart as it was a moment ago. Re-render it from the cart as
    // it is now, the same way assets/cart-drawer.js does after a Rebuy add.
    //
    // Imported HERE rather than at the top of the file on purpose. This module's first job is to
    // strip `auto-open` before assets/cart-drawer.js reads it at `submit`, ~160ms after the click,
    // and a static import would put a dependency fetch in front of that on a cold page. A
    // correction is rare; the hold is on every add.
    const { sectionRenderer } = await import('@theme/section-renderer');
    sectionRenderer.renderSection('cart-drawer-custom', { cache: false });
  } catch (error) {
    console.error('Correcting the bundle swap failed; the cart is left as Rebuy wrote it:', error);
  }
}

/**
 * The swap's removal, clamped to the single unit being upgraded.
 * @param {RequestInfo | URL} input
 * @param {RequestInit} init
 * @param {string} url
 * @returns {Promise<Response>}
 */
async function sendSwapRemoval(input, init, url) {
  let payload;
  try {
    payload = JSON.parse(String(init.body));
  } catch {
    return nativeFetch(input, init);
  }
  if (payload?.quantity !== 0 || typeof payload.id !== 'string') return nativeFetch(input, init);

  // Derived from the URL Rebuy just built rather than hardcoded, so they keep any locale prefix.
  const cartUrl = url.replace('/cart/change.js', '/cart.js');
  const addUrl = url.replace('/cart/change.js', '/cart/add.js');

  // The map first — that is the whole point of keeping it, since a read here would sit on the
  // critical path of a swap the shopper is watching. The read is the fallback, and it answers with
  // the line itself so a successful read is always enough on its own.
  const line = cartLines.get(payload.id) ?? (await lineOnCart(payload.id, cartUrl));

  // Never seen this line and could not read it, or it is not in the cart: let Rebuy's own request
  // through, and record nothing — a repair built on a guess could add a machine the shopper never
  // had.
  if (!line) {
    removedLine = null;
    return nativeFetch(input, init);
  }

  const units = line.quantity;

  // The offer converts ONE unit, so whatever the line held, it must end up holding one less.
  const expected = Math.max(units - 1, 0);
  removedLine = {
    key: payload.id,
    variantId: line.variantId,
    properties: line.properties,
    cartUrl,
    addUrl,
    changeUrl: url,
    expected,
    at: Date.now(),
  };

  // One unit is the case Rebuy's own `quantity: 0` already gets right.
  const body = expected === 0 ? init : { ...init, body: JSON.stringify({ ...payload, quantity: expected }) };
  const seq = ++cartRequestSeq;
  const response = await nativeFetch(input, body);

  // `/cart/change.js` answers with the WHOLE cart, so this is the cheapest and most authoritative
  // refresh the map ever gets — and skipping it is what let a stale quantity clamp a one-unit line
  // during testing, turning the removal into a no-op.
  rememberFrom(response, seq);

  // A removal that did not answer 2xx removed nothing, so there is no postcondition to assert and
  // nothing to put back. Forgetting the line here is what keeps the repair and the reconcile from
  // acting on a swap that never happened — and saying so is what keeps the shopper from clicking
  // into a popup that will never answer.
  if (!response.ok) {
    removedLine = null;
    console.error(`Bundle swap removal failed (${response.status}); the cart is unchanged.`);
    reportSwapOutcome(true);
  }

  return response;
}

/**
 * Any add on the page. Outside a swap this only listens; the swap's own add also answers for the
 * removal that preceded it.
 * @param {RequestInfo | URL} input
 * @param {RequestInit | undefined} init
 * @returns {Promise<Response>}
 */
/**
 * @param {RequestInit | undefined} init
 * @returns {boolean} Whether this add is Rebuy's bundle add for THIS widget.
 *
 * Rebuy stamps the line it writes — `_source: Rebuy`, `_widget_id`, `_attribution` — and that stamp
 * is the only reliable way to tell its half of the swap from an add the shopper made themselves.
 */
const isSwapAdd = (init) =>
  typeof init?.body === 'string' && init.body.includes(`"_widget_id":"${WIDGET_ID}"`);

/**
 * @param {RequestInit | undefined} init
 * @returns {typeof removedLine} The removal this add answers for, or null if it answers for none.
 *
 * TWO WAYS A REMOVAL STOPS BEING CLAIMABLE, and both matter:
 *
 *   it is not Rebuy's add   A swap whose `add.js` never arrives — Rebuy erroring, the shopper
 *                           closing the popup in the ~500ms between the two writes — used to leave
 *                           `removedLine` set for the rest of the page. The NEXT add the shopper
 *                           made was then read as the other half of that swap, and the reconcile
 *                           "corrected" it: the machine they had just added was removed again. A
 *                           second interaction that deletes what the shopper asked for is the worst
 *                           shape this bug can take.
 *   it has expired          Belt and braces for the same case, in case Rebuy ever stops stamping.
 */
function claimSwapRemoval(init) {
  const line = removedLine;
  if (!line) return null;

  if (Date.now() - line.at > SWAP_WINDOW) {
    removedLine = null;
    return null;
  }

  return isSwapAdd(init) ? line : null;
}

async function sendCartAdd(input, init) {
  const pendingRepair = claimSwapRemoval(init);

  const seq = ++cartRequestSeq;
  const response = await nativeFetch(input, init);
  rememberFrom(response, seq);

  // The swap's own add, failing with no removal to undo — the shape a throttled cart takes, since
  // the removal ahead of it failed too. Nothing to repair, but the shopper still needs telling.
  if (!response.ok && !pendingRepair && isSwapAdd(init)) {
    console.error(`Bundle add failed (${response.status}); the cart is unchanged.`);
    reportSwapOutcome(true);
  }

  if (pendingRepair) {
    if (response.ok) {
      // Deliberately not awaited, and deliberately outside this lane slot: awaiting it here would
      // hold the slot its own correction has to queue into, and would put a cart read between the
      // shopper's click and the drawer opening.
      reconcileSwap(pendingRepair);
    } else {
      // The machine is gone and the bundle that was supposed to replace it never landed. Put it
      // back rather than leave the shopper short, and say so.
      console.error(`Bundle add failed (${response.status}); restoring the machine.`);
      reportSwapOutcome(true);
      await restoreRemovedLine();
    }
    removedLine = null;
  }

  return response;
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit | undefined} init
 * @param {string} url
 * @returns {Promise<Response>}
 */
function sendCartWrite(input, init, url) {
  if (swapping && init && isCartChange(url)) return sendSwapRemoval(input, init, url);
  if (isCartAdd(url)) return sendCartAdd(input, init);

  // Someone else's cart write, landing mid-swap. Nothing to rewrite — it just takes its turn.
  return nativeFetch(input, init);
}

window.fetch = function (input, init) {
  const url = typeof input === 'string' ? input : /** @type {any} */ (input)?.url;
  if (typeof url !== 'string') return nativeFetch(input, init);

  // While a swap is live, every cart write goes single-file. Outside one, nothing is queued.
  if (isCartWrite(url, init) && (swapping || laneDepth > 0)) return inLane(() => sendCartWrite(input, init, url));

  if (isCartAdd(url)) return sendCartAdd(input, init);

  if (isCartRead(url)) {
    const seq = ++cartRequestSeq;
    return nativeFetch(input, init).then((response) => {
      rememberFrom(response, seq);
      return response;
    });
  }

  return nativeFetch(input, init);
};
